import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if Supabase is configured
const isSupabaseConfigured = supabaseUrl && supabaseAnonKey && 
  supabaseUrl !== 'undefined' && supabaseAnonKey !== 'undefined';

// Create Supabase client only if configured
export const supabase = isSupabaseConfigured 
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    })
  : null;

// Export configuration status for other components
export { isSupabaseConfigured };

// Log warning in development if Supabase is not configured
if (!isSupabaseConfigured && import.meta.env.DEV) {
  console.warn('Supabase not configured. Auth and database features will be disabled.');
}

// Auth helper functions
export const authHelpers = {
  // Sign up new user
  async signUp(email, password, userData = {}) {
    if (!supabase) {
      return { data: null, error: { message: 'Authentication not configured' } };
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: userData.fullName,
          company_name: userData.companyName,
          position: userData.position,
          phone: userData.phone,
          linkedin_url: userData.linkedinUrl,
          company_website: userData.companyWebsite
        }
      }
    });
    return { data, error };
  },

  // Sign in user
  async signIn(email, password) {
    if (!supabase) {
      return { data: null, error: { message: 'Authentication not configured' } };
    }
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    // If successful, create or update user profile
    if (data.user && !error) {
      await this.ensureUserProfile(data.user);
    }
    
    return { data, error };
  },

  // Ensure user profile exists
  async ensureUserProfile(user) {
    if (!supabase) return;
    try {
      const { data: existingProfile, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching user profile:', fetchError);
        return;
      }

      if (!existingProfile) {
        // Create new profile if it doesn't exist
        const { error: createError } = await supabase
          .from('user_profiles')
          .insert({
            id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
            approval_status: 'pending',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (createError) {
          console.error('Error creating user profile:', createError);
        } else {
          console.log('User profile created for:', user.email);
        }
      } else {
        // Update existing profile to ensure email is present
        const updates = { updated_at: new Date().toISOString() };
        
        // Always update email if it's missing or different
        if (!existingProfile.email || existingProfile.email !== user.email) {
          updates.email = user.email;
        }
        
        // Update full_name if it's missing
        if (!existingProfile.full_name && user.email) {
          updates.full_name = user.email.split('@')[0];
        }

        if (Object.keys(updates).length > 1) { // More than just updated_at
          const { error: updateError } = await supabase
            .from('user_profiles')
            .update(updates)
            .eq('id', user.id);
          
          if (updateError) {
            console.error('Error updating user profile:', updateError);
          } else {
            console.log('User profile updated for:', user.email);
          }
        }
      }
    } catch (error) {
      console.error('Error ensuring user profile:', error);
    }
  },

  // Sign out user
  async signOut() {
    if (!supabase) return { error: null };
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current user
  async getCurrentUser() {
    if (!supabase) return { user: null, error: null };
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  // Get user profile
  async getUserProfile(userId) {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        *,
        user_subscriptions (
          id,
          status,
          expires_at,
          subscription_plans (
            name,
            features,
            webinar_access,
            learning_paths_access
          )
        )
      `)
      .eq('id', userId)
      .single();
    return { data, error };
  },

  // Update user profile
  async updateUserProfile(userId, updates) {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  },

  // Check if user has subscription access
  async checkSubscriptionAccess(userId, requiredLevel = 'free') {
    if (requiredLevel === 'free') return { hasAccess: true };

    try {
      // For now, return basic access since subscription tables might not be set up
      // This prevents the 406 errors while still allowing basic functionality
      return { 
        hasAccess: true, 
        subscription: {
          planName: 'Basic',
          webinarAccess: true,
          learningPathsAccess: true,
          expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year from now
        }
      };
    } catch (error) {
      console.warn('Subscription check failed, returning basic access:', error);
      return { 
        hasAccess: true, 
        subscription: {
          planName: 'Basic',
          webinarAccess: true,
          learningPathsAccess: true,
          expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        }
      };
    }
  }
};

// Database helper functions
export const dbHelpers = {
  // Resources
  async getResources(filters = {}) {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('resources')
      .select('*')
      .eq('status', 'published')
      .order('created_at', { ascending: false });

    if (filters.contentType) {
      query = query.eq('content_type', filters.contentType);
    }
    if (filters.audience) {
      query = query.eq('audience', filters.audience);
    }
    if (filters.featured) {
      query = query.eq('featured', true);
    }

    const { data, error } = await query;
    return { data, error };
  },

  async getResourceById(id) {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('resources')
      .select('*')
      .eq('id', id)
      .single();
    return { data, error };
  },

  async createResource(resourceData) {
    if (!supabase) return { data: null, error: { message: 'Database not configured' } };
    const { data, error } = await supabase
      .from('resources')
      .insert([resourceData])
      .select()
      .single();
    return { data, error };
  },

  async updateResource(id, updates) {
    if (!supabase) return { data: null, error: { message: 'Database not configured' } };
    const { data, error } = await supabase
      .from('resources')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async deleteResource(id) {
    if (!supabase) return { data: null, error: { message: 'Database not configured' } };
    const { data, error } = await supabase
      .from('resources')
      .delete()
      .eq('id', id);
    return { data, error };
  },

  // Learning Paths
  async getLearningPaths(filters = {}) {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('learning_paths')
      .select(`
        *,
        learning_path_modules (
          id,
          title,
          duration_minutes
        )
      `)
      .eq('status', 'published')
      .order('created_at', { ascending: false });

    if (filters.level) {
      query = query.eq('level', filters.level);
    }

    const { data, error } = await query;
    return { data, error };
  },

  async getLearningPathById(id) {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('learning_paths')
      .select(`
        *,
        learning_path_modules (
          *
        )
      `)
      .eq('id', id)
      .single();
    return { data, error };
  },

  async enrollInLearningPath(userId, learningPathId) {
    if (!supabase) return { data: null, error: { message: 'Database not configured' } };
    // First get all modules for this learning path
    const { data: modules, error: modulesError } = await supabase
      .from('learning_path_modules')
      .select('id')
      .eq('learning_path_id', learningPathId);

    if (modulesError) return { error: modulesError };

    // Create progress entries for all modules
    const progressEntries = modules.map(module => ({
      user_id: userId,
      learning_path_id: learningPathId,
      module_id: module.id,
      status: 'not_started',
      progress_percentage: 0
    }));

    const { data, error } = await supabase
      .from('user_learning_progress')
      .insert(progressEntries)
      .select();

    // Update enrolled count
    if (!error) {
      await supabase
        .from('learning_paths')
        .update({ enrolled_count: supabase.sql`enrolled_count + 1` })
        .eq('id', learningPathId);
    }

    return { data, error };
  },

  // Webinars
  async getWebinars(filters = {}) {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('webinars')
      .select('*')
      .order('scheduled_date', { ascending: true });

    if (filters.upcoming) {
      query = query.gt('scheduled_date', new Date().toISOString());
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;
    return { data, error };
  },

  async registerForWebinar(userId, webinarId) {
    if (!supabase) return { data: null, error: { message: 'Database not configured' } };
    const { data, error } = await supabase
      .from('webinar_registrations')
      .insert([{
        user_id: userId,
        webinar_id: webinarId,
        registration_date: new Date().toISOString()
      }])
      .select()
      .single();

    // Update registered count
    if (!error) {
      await supabase
        .from('webinars')
        .update({ registered_count: supabase.sql`registered_count + 1` })
        .eq('id', webinarId);
    }

    return { data, error };
  },

  // Blogs
  async getBlogs(filters = {}) {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('blogs')
      .select('*')
      .eq('status', 'published')
      .order('created_at', { ascending: false });

    if (filters.category) {
      query = query.eq('category', filters.category);
    }
    if (filters.featured) {
      query = query.eq('featured', true);
    }

    const { data, error } = await query;
    return { data, error };
  },

  async getBlogBySlug(slug) {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('blogs')
      .select('*')
      .eq('slug', slug)
      .single();

    // Increment view count
    if (data && !error) {
      await supabase
        .from('blogs')
        .update({ view_count: supabase.sql`view_count + 1` })
        .eq('id', data.id);
    }

    return { data, error };
  },

  // User Activities
  async trackUserActivity(userId, activityType, resourceType, resourceId, metadata = {}) {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('user_activities')
      .insert([{
        user_id: userId,
        activity_type: activityType,
        resource_type: resourceType,
        resource_id: resourceId,
        metadata,
        created_at: new Date().toISOString()
      }]);
    return { data, error };
  },

  // Content Reviews
  async addContentReview(userId, contentType, contentId, rating, reviewText) {
    if (!supabase) return { data: null, error: { message: 'Database not configured' } };
    const { data, error } = await supabase
      .from('content_reviews')
      .upsert({
        user_id: userId,
        content_type: contentType,
        content_id: contentId,
        rating,
        review_text: reviewText
      })
      .select()
      .single();
    return { data, error };
  },

  // Admin Functions
  async getAdminDashboardStats() {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('admin_dashboard_stats')
      .select('*')
      .single();
    return { data, error };
  },

  async getUserDashboardStats(userId) {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('user_dashboard_stats')
      .select('*')
      .eq('user_id', userId)
      .single();
    return { data, error };
  },

  // Subscription Management
  async getSubscriptionPlans() {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .order('price_monthly', { ascending: true });
    return { data, error };
  },

  async createSubscription(userId, planId, paymentData) {
    if (!supabase) return { data: null, error: { message: 'Database not configured' } };
    const plan = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('id', planId)
      .single();

    if (plan.error) return { error: plan.error };

    const expiresAt = new Date();
    expiresAt.setFullYear(expiresAt.getFullYear() + 1); // 1 year subscription

    const { data, error } = await supabase
      .from('user_subscriptions')
      .insert([{
        user_id: userId,
        plan_id: planId,
        status: 'active',
        expires_at: expiresAt.toISOString(),
        payment_method: paymentData.method,
        payment_id: paymentData.id
      }])
      .select()
      .single();

    // Update user profile subscription status
    if (!error) {
      await supabase
        .from('user_profiles')
        .update({
          subscription_status: plan.data.name.toLowerCase(),
          subscription_expires_at: expiresAt.toISOString()
        })
        .eq('id', userId);
    }

    return { data, error };
  }
};

export default supabase;
